<template>
    <div v-if="!validation" class="position-relative">
        <textarea
            class="ui-form-textarea"
            :class="{ 'disable-resize': disableresize }"
            :value="value"
            :placeholder="placeholder"
            v-bind="attrs"
            @input="input"
            v-on="listeners"
        >
            <slot />
        </textarea>
        <span v-if="max" class="counter" :class="maxLength">{{ value.length }} / {{ max }}</span>
    </div>

    <div v-else>
        <div class="icon-wrapper">
            <textarea
                :id="id"
                class="ui-form-textarea"
                :class="{ error: hasError, warning: hasWarning, valid: isValid }"
                :value="value"
                v-bind="attrs"
                :validation="validation"
                @input="input"
                @change="validate"
                @blur="validate"
                v-on="listeners"
            />

            <div v-if="hasError || hasWarning || isValid" class="validation-icons">
                <UIIcon v-if="isValid" name="check-circle" class="valid-icon" aria-hidden="true" tabindex="-1" />
                <UIIcon v-if="hasError" name="exclamation-circle" class="error-icon" aria-hidden="true" tabindex="-1" />
                <UIIcon
                    v-if="hasWarning"
                    name="exclamation-triangle"
                    class="warning-icon"
                    aria-hidden="true"
                    tabindex="-1"
                />
            </div>
        </div>
        <span v-if="hasError" class="error-text small">
            <slot name="error-message" />
        </span>
        <span v-if="hasWarning" class="warning-text small">
            <slot name="warning-message" />
        </span>
    </div>
</template>

<script>
import { UIIcon } from '@dundle/ui/components';
import ValidationMixin from '~/mixins/ValidationMixin';
export default {
    components: {
        UIIcon,
    },
    mixins: [ValidationMixin],

    inheritAttrs: false,
    props: ['value', 'id', 'validation', 'placeholder', 'max', 'disableresize'],

    computed: {
        listeners() {
            const { input, ...listeners } = this.$listeners;
            return listeners;
        },
        attrs() {
            const { ...attrs } = this.$attrs;
            return attrs;
        },
        maxLength() {
            return this.value.length >= this.max ? 'error-text' : '';
        },
    },

    methods: {
        input(e) {
            this.$emit('input', e.target.value);
            if (this.hasError || this.hasWarning) this.validate(e);
        },
        validate(e) {
            this.validateOnChange({ value: e.target.value });
        },
    },
};
</script>

<style lang="scss" scoped>
.ui-form-textarea {
    display: block;
    width: 100%;
    height: 100%;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.75;
    color: $color-grey-6;
    background-color: $color-form-bg;
    background-clip: padding-box;
    border: 1px solid $color-grey-3;
    border-radius: 0.25rem;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    overflow: visible;

    &:focus {
        color: $color-grey-6;
        background-color: $color-form-bg;
        border-color: lighten($color-primary, 30%);
        outline: 0;
        box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.2rem rgba($color-primary, 0.25);
    }

    &[disabled],
    &[readonly] {
        background-color: rgba(0, 0, 0, 0.05);
    }

    font-size: 16px !important;
    padding: 0.5rem 1rem;

    &.error {
        border-color: $color-error;
        background-color: $color-error-bg;
    }

    &.warning {
        border-color: $color-warning;
    }

    &.valid {
        border-color: $color-valid;
        background-color: $color-valid-bg;
    }
}

.error-text {
    color: $color-error;
}
.warning-text {
    color: $color-warning;
}

.validation-icons {
    position: absolute;
    line-height: 2.75;
}
.icon-wrapper {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    svg {
        margin-right: 0.75rem;
    }
    .warning-icon {
        color: $color-warning;
    }
    .error-icon {
        color: $color-error;
    }
    .valid-icon {
        color: $color-valid;
    }
}
.counter {
    position: absolute;
    right: 0;
    margin-top: 0.2rem;
}

.disable-resize {
    resize: none;
}
</style>
